import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "./SideList.module.css";

import { useModuleContext } from "./ModuleContext";

// Renders a module's result list into the side column next to the map
// (desktop: right of the map, where the detail panel opens; mobile: below
// the map). The module keeps all its state — this only moves the markup.
export function SideList({ children }) {
  const { sideSlot, setHasSideList } = useModuleContext();

  // tells ParentResult a list is shown, so the detail panel offers "back"
  useEffect(() => {
    setHasSideList(true);
    return () => setHasSideList(false);
  }, [setHasSideList]);

  if (!sideSlot) return null;
  return createPortal(<div className={styles.list}>{children}</div>, sideSlot);
}

// heading above a group of rows, e.g. a day in the event calendar. `header`
// (any node, e.g. <SideListDateHeader>) replaces the plain text `title` and
// sticks to the top while the list scrolls.
export function SideListGroup({ title, header, children }) {
  return (
    <div className={styles.group}>
      {header ? (
        <div className={styles.groupHeader}>{header}</div>
      ) : (
        title && <div className={styles.groupTitle}>{title}</div>
      )}
      {children}
    </div>
  );
}

// calendar-style day header: date tile (weekday / day / month) plus a label
// ("Heute", "Freitag") and an optional count line ("3 Termine")
export function SideListDateHeader({ weekday, day, month, label, count }) {
  return (
    <div className={styles.dateHeader}>
      <span className={styles.dateTile} aria-hidden="true">
        <span className={styles.dateTileWeekday}>{weekday}</span>
        <span className={styles.dateTileDay}>{day}</span>
        <span className={styles.dateTileMonth}>{month}</span>
      </span>
      <span className={styles.dateText}>
        <span className={styles.dateLabel}>{label}</span>
        {count && <span className={styles.dateCount}>{count}</span>}
      </span>
    </div>
  );
}

// one clickable entry. Plain: title + optional prefix (time) and meta line;
// `icon` = category icon URL shown before the title (same as on the map).
// card: framed entry with optional `aside` column on the left (e.g. time),
// `badge` (e.g. "abgesagt") and `thumbUrl` picture on the right.
export function SideListItem({
  prefix,
  icon,
  title,
  meta,
  aside,
  badge,
  thumbUrl,
  card,
  active,
  muted,
  onClick,
}) {
  const classes = [styles.item];
  if (card) classes.push(styles.itemCard);
  if (active) classes.push(styles.itemActive);
  if (muted) classes.push(styles.itemMuted);

  return (
    <button type="button" className={classes.join(" ")} onClick={onClick}>
      {aside && <span className={styles.itemAside}>{aside}</span>}
      <span className={styles.itemBody}>
        <span className={styles.itemTitle}>
          {icon && <img src={icon} alt="" className={styles.itemIcon} />}
          {prefix && <span className={styles.itemPrefix}>{prefix}</span>}
          {title}
        </span>
        {meta && <span className={styles.itemMeta}>{meta}</span>}
        {badge && <span className={styles.itemBadge}>{badge}</span>}
      </span>
      {thumbUrl && <img src={thumbUrl} alt="" className={styles.itemThumb} />}
    </button>
  );
}

export function SideListMessage({ children }) {
  return <div className={styles.message}>{children}</div>;
}
